/**
 * Tag-basierte Farbverläufe und Initialen für Thumbnails.
 */

const TAG_GRADIENTS: Record<string, string> = {
  NIS2:     "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
  DORA:     "linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)",
  DSGVO:    "linear-gradient(135deg, #d97706 0%, #fbbf24 100%)",
  CRA:      "linear-gradient(135deg, #dc2626 0%, #f87171 100%)",
  "AI-Act": "linear-gradient(135deg, #9333ea 0%, #c084fc 100%)",
  eIDAS:    "linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)",
  MiCA:     "linear-gradient(135deg, #ea580c 0%, #fb923c 100%)",
  BaFin:    "linear-gradient(135deg, #1e40af 0%, #60a5fa 100%)",
};

const DEFAULT_GRADIENT = "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)";

/**
 * Gibt einen CSS-Gradient passend zum ersten erkannten Tag zurück.
 */
export function getTagGradient(tags: string[]): string {
  for (const tag of tags) {
    const upper = tag.toUpperCase();
    for (const [key, gradient] of Object.entries(TAG_GRADIENTS)) {
      if (upper === key.toUpperCase() || upper.includes(key.toUpperCase())) {
        return gradient;
      }
    }
  }
  return DEFAULT_GRADIENT;
}

/**
 * Extrahiert eine kurze Abkürzung für den Thumbnail-Text.
 * Priorität: Erster Tag > Quelle/Author-Initiale > "?"
 */
export function getThumbLabel(tags: string[], author: string | null): string {
  if (tags.length > 0) {
    const tag = tags[0];
    // Bekannte kurze Tags direkt verwenden
    if (tag.length <= 5) return tag;
    // Längere Tags kürzen
    return tag.slice(0, 4).toUpperCase();
  }
  if (author) {
    // E-Mail: Teil vor @ nehmen, ersten Buchstaben
    const clean = author.replace(/<.*>/, "").replace(/"/g, "").trim();
    const name = clean.split("@")[0];
    return name.charAt(0).toUpperCase();
  }
  return "?";
}

/**
 * Extrahiert die Domain aus einer E-Mail-Adresse oder Author-Header.
 * z.B. "Max Mustermann <info@bafin.de>" → "bafin.de"
 */
export function getAuthorDomain(author: string | null): string | null {
  if (!author) return null;
  // Versuche E-Mail zu extrahieren
  const emailMatch = author.match(/[\w.+-]+@([\w.-]+\.\w+)/);
  if (emailMatch) return emailMatch[1].toLowerCase();
  return null;
}

/**
 * Gibt eine Favicon-URL für eine Domain zurück (Google Favicon Service).
 */
export function getFaviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
}

/**
 * Kuratierte Stock-Bilder pro Regulierungsthema.
 * Anzahl der verfügbaren Varianten pro Tag.
 */
const TOPIC_IMAGES: Record<string, number> = {
  NIS2: 3,
  DORA: 3,
  DSGVO: 3,
  CRA: 2,
  "AI-Act": 3,
  eIDAS: 2,
  MiCA: 2,
  BaFin: 3,
};
const DEFAULT_IMAGE_COUNT = 3;

/**
 * Gibt eine thematische Bild-URL passend zum Tag zurück.
 * Nutzt item.id für Variation (verschiedene Bilder pro Artikel).
 */
export function getTopicImage(tags: string[], itemId: number): string {
  for (const tag of tags) {
    const upper = tag.toUpperCase();
    for (const [key, count] of Object.entries(TOPIC_IMAGES)) {
      if (upper === key.toUpperCase() || upper.includes(key.toUpperCase())) {
        const idx = (itemId % count) + 1;
        return `/images/topics/${key.toLowerCase().replace("-", "")}-${idx}.jpg`;
      }
    }
  }
  const idx = (itemId % DEFAULT_IMAGE_COUNT) + 1;
  return `/images/topics/default-${idx}.jpg`;
}
